import React from 'react';
import {
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';

const PRIMARY = '#8BC34A';
const PRIMARY_DARK = '#5b9b1e';

export function EmailSettingsPageHeader({
  icon,
  title,
  subtitle,
  refreshing = false,
  refreshLabel,
  onRefresh,
  primaryLabel,
  onPrimary,
  primaryIcon = <AddIcon />,
  primaryDisabled = false,
  actions,
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 2,
        mb: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '10px',
            backgroundColor: '#eef7e3',
            color: PRIMARY_DARK,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography
            sx={{ fontSize: 22, fontWeight: 700, color: '#333', lineHeight: 1.1 }}
          >
            {title}
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#888', mt: 0.4 }}>
            {subtitle}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        {onRefresh && (
          <Tooltip title={refreshLabel || ''}>
            <span>
              <IconButton
                onClick={onRefresh}
                disabled={refreshing}
                sx={{
                  border: '1px solid #cfcfcf',
                  borderRadius: '8px',
                  color: PRIMARY,
                  width: 40,
                  height: 40,
                  '&:hover': {
                    backgroundColor: '#f1f8e9',
                    borderColor: PRIMARY,
                  },
                }}
              >
                {refreshing ? (
                  <CircularProgress size={18} sx={{ color: PRIMARY }} />
                ) : (
                  <RefreshIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        )}
        {actions}
        {primaryLabel && onPrimary && (
          <Button
            variant="contained"
            startIcon={primaryIcon}
            onClick={onPrimary}
            disabled={primaryDisabled}
            sx={{
              textTransform: 'none',
              backgroundColor: PRIMARY,
              '&:hover': { backgroundColor: '#7cb342' },
            }}
          >
            {primaryLabel}
          </Button>
        )}
      </Box>
    </Box>
  );
}

export function EmailSettingsTabs({ value, onChange, tabs, disabled = false }) {
  return (
    <Tabs
      value={value}
      onChange={(_event, nextValue) => onChange(nextValue)}
      variant="scrollable"
      scrollButtons="auto"
      sx={{
        mb: 2,
        borderBottom: '1px solid #e8e8e8',
        '& .MuiTabs-indicator': { backgroundColor: PRIMARY },
      }}
    >
      {tabs.map(tab => (
        <Tab
          key={tab.value}
          value={tab.value}
          disabled={disabled}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            '&.Mui-selected': { color: PRIMARY_DARK },
          }}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {tab.label}
              <Badge
                badgeContent={tab.count}
                showZero
                color="default"
                sx={{
                  '& .MuiBadge-badge': {
                    backgroundColor: `${tab.badgeColor || PRIMARY} !important`,
                    color: '#fff !important',
                    fontWeight: 700,
                    position: 'static',
                    transform: 'none',
                  },
                }}
              />
            </Box>
          }
        />
      ))}
    </Tabs>
  );
}

export function EmailSettingsSearch({
  value,
  onChange,
  onSearch,
  onClear,
  placeholder,
  clearLabel,
  disabled = false,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1.25,
        mb: 1.5,
        border: '1px solid #e0e0e0',
        borderRadius: '10px',
        backgroundColor: '#fff',
      }}
    >
      <TextField
        fullWidth
        size="small"
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={event => onChange(event.target.value)}
        onKeyDown={event => event.key === 'Enter' && onSearch()}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: '#9e9e9e', fontSize: 20 }} />
            </InputAdornment>
          ),
        }}
      />
      <Button
        variant="contained"
        disabled={disabled}
        onClick={onSearch}
        sx={{
          minWidth: 42,
          height: 40,
          px: 1.25,
          backgroundColor: PRIMARY,
          '&:hover': { backgroundColor: '#7cb342' },
        }}
        aria-label={placeholder}
      >
        <SearchIcon />
      </Button>
      <Tooltip title={clearLabel || ''}>
        <span>
          <IconButton
            disabled={disabled}
            onClick={onClear}
            sx={{
              border: '1px solid #d7d7d7',
              borderRadius: '8px',
              width: 40,
              height: 40,
            }}
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Paper>
  );
}

export function EmailSettingsStatusChip({ active, activeLabel, inactiveLabel }) {
  return (
    <Chip
      size="small"
      label={active ? activeLabel : inactiveLabel}
      sx={{
        borderRadius: '6px',
        backgroundColor: active ? '#eef7e3' : '#f1f1f1',
        color: active ? PRIMARY_DARK : '#777',
        fontWeight: 600,
        fontSize: 12,
      }}
    />
  );
}

export function EmailSettingsTable({
  columns,
  rows,
  renderCells,
  getRowKey = row => row.id,
  onRowClick,
  minWidth = 760,
}) {
  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        border: '1px solid #e0e0e0',
        borderRadius: '12px',
        backgroundColor: '#fff',
      }}
    >
      <Table size="small" sx={{ minWidth }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#fafafa' }}>
            {columns.map(column => (
              <TableCell
                key={column.key}
                align={column.align || 'left'}
                sx={{
                  width: column.width,
                  py: 1.25,
                  px: 1.5,
                  fontSize: 12,
                  color: '#888',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                }}
              >
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => {
            const cells = renderCells(row);
            return (
              <TableRow
                hover={Boolean(onRowClick)}
                key={getRowKey(row)}
                onClick={onRowClick ? event => onRowClick(event, row) : undefined}
                sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
              >
                {columns.map((column, index) => (
                  <TableCell
                    key={column.key}
                    align={column.align || 'left'}
                    sx={{
                      py: 1.5,
                      px: 1.5,
                      fontSize: 13,
                      color: '#333',
                      borderColor: '#f0f0f0',
                      verticalAlign: 'middle',
                    }}
                  >
                    {cells[index]}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function EmailSettingsListSkeleton({ columns = 5, rows = 5 }) {
  return (
    <Box
      sx={{
        border: '1px solid #e0e0e0',
        borderRadius: '12px',
        p: 2,
        backgroundColor: '#fff',
      }}
    >
      {[...Array(rows)].map((_, rowIndex) => (
        <Box
          key={rowIndex}
          sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.2 }}
        >
          {[...Array(columns)].map((__, columnIndex) =>
            columnIndex === columns - 1 ? (
              <Skeleton
                key={columnIndex}
                variant="rounded"
                width={columnIndex === 0 ? 50 : 86}
                height={24}
                animation="wave"
              />
            ) : (
              <Skeleton
                key={columnIndex}
                variant="text"
                width={`${Math.max(12, 28 - columnIndex * 3)}%`}
                animation="wave"
              />
            )
          )}
        </Box>
      ))}
    </Box>
  );
}

export function EmailSettingsEmptyState({
  icon,
  title,
  description,
  actionLabel,
  onAction,
}) {
  return (
    <Box
      sx={{
        textAlign: 'center',
        py: 8,
        border: '1px solid #e0e0e0',
        borderRadius: '12px',
        backgroundColor: '#fff',
      }}
    >
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: '50%',
          backgroundColor: '#f5f5f5',
          color: '#bdbdbd',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#555', mb: 0.5 }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: 14, color: '#888', mb: 2 }}>
        {description}
      </Typography>
      {actionLabel && onAction && (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onAction}
          sx={{
            textTransform: 'none',
            backgroundColor: PRIMARY,
            '&:hover': { backgroundColor: '#7cb342' },
          }}
        >
          {actionLabel}
        </Button>
      )}
    </Box>
  );
}

export function EmailSettingsPanel({ title, subtitle, action, children, sx }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 3 },
        mb: 2,
        border: '1px solid #e0e0e0',
        borderRadius: '12px',
        backgroundColor: '#fff',
        ...sx,
      }}
    >
      {(title || action) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            mb: 2.5,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 17, fontWeight: 700, color: '#444' }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography sx={{ fontSize: 13, color: '#888', mt: 0.4 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {action}
        </Box>
      )}
      {children}
    </Paper>
  );
}

export function EmailSettingsFormSkeleton({ sections = 3 }) {
  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Skeleton variant="rounded" width={44} height={44} animation="wave" />
          <Box>
            <Skeleton variant="text" width={230} height={30} animation="wave" />
            <Skeleton variant="text" width={320} height={18} animation="wave" />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Skeleton variant="rounded" width={92} height={40} animation="wave" />
          <Skeleton variant="rounded" width={110} height={40} animation="wave" />
        </Box>
      </Box>
      {[...Array(sections)].map((_, index) => (
        <Paper
          key={index}
          elevation={0}
          sx={{
            p: 3,
            mb: 2,
            border: '1px solid #e0e0e0',
            borderRadius: '12px',
          }}
        >
          <Skeleton variant="text" width={180} height={28} animation="wave" />
          <Skeleton
            variant="text"
            width="45%"
            height={18}
            animation="wave"
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Skeleton
              variant="rounded"
              width={index === 1 ? '100%' : '65%'}
              height={index === 1 ? 210 : 54}
              animation="wave"
            />
            {index !== 1 && (
              <Skeleton variant="rounded" width={180} height={54} animation="wave" />
            )}
          </Box>
        </Paper>
      ))}
    </Box>
  );
}
